import React, { PropsWithChildren, useEffect, useState } from 'react';

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Box, SxProps, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { GridRowId, GridSelectionModel } from '@mui/x-data-grid-pro';

import useTableContext from '../../../admin-portal/BaseTabsRouting/Headers/HeaderGrid/useTableContext';
import CreateButton from '../../CreateButton/CreateButton';
import { AdditionalButton } from '../DataGrid';
import GridHeader from './styled/GridHeader';

type Props = {
  selectionModel: GridSelectionModel;
  setShowDeleteConfirmation: React.Dispatch<boolean>;
  setDeleteSelection: React.Dispatch<boolean>;
  onAddClickTitle?: string;
  additionalButtons?: AdditionalButton[];
  onAddClick?: () => void;
  onDeleteClick?: (idsList: GridRowId[]) => void;
  disableActions?: boolean;
  checkboxSelection?: boolean;
  title?: string | React.ReactNode;
  headerContent?: React.ReactNode;
  sx?: SxProps;
};

const addAdditionalButtons = (additionalButtons: AdditionalButton[]) => {
  return additionalButtons?.map((additionalButton) => (
    <Button
      startIcon={additionalButton.startIcon ?? null}
      key={additionalButton.label}
      variant="contained"
      onClick={additionalButton.onClick}
      disabled={additionalButton.disabled || false}
      color={additionalButton.color}
    >
      {additionalButton.label}
    </Button>
  ));
};

const DataGridHeader = ({
  onAddClickTitle,
  additionalButtons,
  onAddClick,
  onDeleteClick,
  title,
  selectionModel,
  setShowDeleteConfirmation,
  setDeleteSelection,
  disableActions,
  checkboxSelection,
  headerContent,
  sx
}: Props): JSX.Element => {
  const { t } = useTranslation('common');

  return (
    <GridHeader sx={sx}>
      <Box sx={{ display: 'flex' }}>
        {checkboxSelection && onDeleteClick && !disableActions && (
          <Button
            variant="contained"
            onClick={() => {
              setDeleteSelection(true);
              setShowDeleteConfirmation(true);
            }}
            disabled={selectionModel.length === 0}
          >
            {t('deleteSelection')}
          </Button>
        )}
        {title && !checkboxSelection && (
          <Typography variant="h3">{title}</Typography>
        )}
        {headerContent}
      </Box>
      <Box ml="auto" display="flex" gap={1}>
        {onAddClick && !disableActions && (
          <CreateButton onClick={onAddClick} label={onAddClickTitle} />
        )}
        {!disableActions &&
          additionalButtons &&
          addAdditionalButtons(additionalButtons)}
      </Box>
    </GridHeader>
  );
};

export default DataGridHeader;

export const DataGridHeaderPortal: React.FC<PropsWithChildren<object>> = (
  props
) => {
  const { contentNodeForButtons } = useTableContext();
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (contentNodeForButtons?.current) setLoaded(true);
  }, [contentNodeForButtons?.current]);

  return loaded ? (
    createPortal(
      <>{props.children}</>,
      contentNodeForButtons.current as HTMLDivElement
    )
  ) : (
    <></>
  );
};
